import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { languageService } from '@unpispas/upp-base';
import { PlacePanel } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { FloorArea } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

/** Main work tabs for the Pinpad terminal (RN-PTF03-03). */
export type PtfWorkTab = 'tables' | 'tickets';

const _workTabs: ReadonlyArray<PtfWorkTab> = ['tables', 'tickets'];

/**
 * Pinpad work screen: Mesas / Tickets panes and filtered PlacePanel routing
 * (RN-PTF03-02 / RN-PTF03-03). No cash-control UI (POS only). Guest pay asks
 * use the shared topbar WAITER strip and table Attention (POS parity).
 * Ticket detail and cart overlays are hosted by home; the tickets list is an
 * app TemplateRef (RN-PPS-03). Tab chrome on home footer.
 */
@Component({
    selector: 'upp-pinpad-work',
    templateUrl: './upp-work.html',
    styleUrl: './upp-work.scss',
})
export class UppPinpadWorkComponent implements OnInit, OnDestroy {
    /** App-provided tickets list (`app-ptf-tickets`). */
    @Input() ticketsTpl: TemplateRef<{ $implicit: UppPinpadWorkComponent }> | null = null;

    private _ontab: Subscription | null = null;
    /** Once true, the tickets pane stays in the DOM (hidden when inactive). */
    private _ticketsMounted = false;
    /** Floor hydration reached Stage ≥ 2 (C3 / CA-T14-04). */
    private _floorReady = false;
    private _onstage: Subscription | null = null;
    private _placeRefresh: Subscription | null = null;
    private _placeChanged: Subscription | null = null;
    /** Optional mesa filter applied when opening Tickets from a multi-ticket table. */
    private _tableFilter: QrCodeView | null = null;
    /** Selected floor plan area for Mesas (null = table grid). */
    private _floorSelected: FloorArea | null = null;
    /** Whether the horizontal floor picker is visible under the home header. */
    private _showFloorRow = false;

    constructor(
        private lang: languageService,
        private data: dataService,
        private change: ChangeDetectorRef,
        public view: viewService,
    ) {}

    get tab(): PtfWorkTab {
        const current = this.view.MainTab;
        // Shared topbar OnWaiter uses POS tab id `placetables`.
        if (current === 'placetables') {
            return 'tables';
        }
        return this._IsWorkTab(current) ? current : 'tables';
    }

    /** Whether the tickets pane has been opened at least once (CA-T04-03). */
    get TicketsMounted(): boolean {
        return this._ticketsMounted;
    }

    get place(): PlaceView | null {
        return this.data.alives.get(this.data.place) as PlaceView;
    }

    /**
     * Mesas/Tickets content may paint once floor sync stage ≥ 2 (C3).
     * Until then Work shows a brief loading state.
     */
    get FloorReady(): boolean {
        return this._floorReady;
    }

    /** Mesa filter for the Tickets tab (cleared when the operator opens Tickets from the bar). */
    get TableFilter(): QrCodeView | null {
        return this._tableFilter;
    }

    set TableFilter(value: QrCodeView | null) {
        this._tableFilter = value;
        this.change.markForCheck();
    }

    /** Floor plan areas of the place (same source as POS tab-tables). */
    get Floors(): FloorArea[] {
        return this.place?.DrawAreas ?? [];
    }

    /** Selected floor for Mesas; null shows the table grid. */
    get FloorSelected(): FloorArea | null {
        return this._floorSelected;
    }

    /**
     * Map icon in the home header: Mesas tab, floors exist, no detail/cart overlay.
     */
    get ShowFloorToggle(): boolean {
        return this.IsTab('tables') && !this.HideTabs && this.Floors.length > 0;
    }

    /** Horizontal floor picker under the home header (POS parity). */
    get ShowFloorRow(): boolean {
        return this._showFloorRow && this.ShowFloorToggle;
    }

    /** Ticket detail open — home hosts `upp-ticket-edit` in the shell panel-content (RN-PTF03-02). */
    get ShowDetail(): boolean {
        return this.view.Mode === 'VIEW' && this.view.ScndTab === 'ticket';
    }

    /**
     * Table Attention / edit open — home hosts `upp-table-edit` (POS parity for guest pay asks).
     */
    get ShowTable(): boolean {
        return this.view.Mode === 'VIEW' && this.view.ScndTab === 'table';
    }

    /** Cart open — home hosts cart in the shell panel-content (catalog / ticket EDIT / product). */
    get ShowCart(): boolean {
        return this.view.Mode === 'CART';
    }

    /** Hide home header/tabs while detail, table Attention or cart fills the shell (POS mobile). */
    get HideTabs(): boolean {
        return this.ShowDetail || this.ShowTable || this.ShowCart;
    }

    get DetailTicket(): TicketView | null {
        const args = this.view.PanelArg;
        return (args && args['ticket']) ? (args['ticket'] as TicketView) : null;
    }

    get DetailTable(): QrCodeView | null {
        const args = this.view.PanelArg;
        return (args && args['table']) ? (args['table'] as QrCodeView) : null;
    }

    get DetailMode(): 'EDIT' | 'VIEW' {
        const args = this.view.PanelArg;
        const mode = args && args['mode'];
        return mode === 'EDIT' ? 'EDIT' : 'VIEW';
    }

    /**
     * Open tickets for the place or one mesa (DD-03-05):
     * `status ≠ 'PR'` and `IsOpened`. Drafts stay invisible until payment/send leaves `PR`.
     * Callers may further require `IsRecent`.
     */
    OpenTickets(table?: QrCodeView | null): Array<TicketView> {
        const source = table
            ? table.tickets
            : (this.place?.tickets ?? []);
        return source.filter((t) => t.status !== 'PR' && t.IsOpened);
    }

    tr(key: string): string {
        return this.lang.tr(key);
    }

    IsTab(name: PtfWorkTab): boolean {
        return this.tab === name;
    }

    IsFloorSelected(area: FloorArea | null): boolean {
        return this._floorSelected === area;
    }

    /** Toggles the horizontal floor picker (header map icon). */
    OnFloorOptions(): void {
        if (this.Floors.length === 0) {
            return;
        }
        this._showFloorRow = !this._showFloorRow;
        this.change.markForCheck();
    }

    /** Selects grid (null) or a floor area for the Mesas pane. */
    OnSelectFloor(area: FloorArea | null): void {
        this._floorSelected = area;
        this.change.markForCheck();
    }

    ngOnInit(): void {
        this.view.Mode = 'VIEW';
        if (!this._IsWorkTab(this.view.MainTab)) {
            this.view.SetMainTab('VIEW', 'tables');
        }
        this._SyncDeferredPanes(this.tab);
        this._SyncFloorReady(this.data.RefreshStage);
        this._BindPlaceRefresh();

        this._ontab = this.view.OnTabChanged.subscribe(() => {
            this._NormalizeTopbarTablesTab();
            this._SyncDeferredPanes(this.tab);
            this.change.markForCheck();
        });
        this._onstage = this.data.OnRefreshStage.subscribe((stage) => {
            this._SyncFloorReady(stage);
            this.change.markForCheck();
        });
        this._placeChanged = this.data.OnPlaceChanged.subscribe(() => {
            this._BindPlaceRefresh();
            this.change.markForCheck();
        });
    }

    ngOnDestroy(): void {
        if (this._ontab) {
            this._ontab.unsubscribe();
            this._ontab = null;
        }
        if (this._onstage) {
            this._onstage.unsubscribe();
            this._onstage = null;
        }
        if (this._placeChanged) {
            this._placeChanged.unsubscribe();
            this._placeChanged = null;
        }
        if (this._placeRefresh) {
            this._placeRefresh.unsubscribe();
            this._placeRefresh = null;
        }
    }

    /** Keeps floor chrome in sync when place children refresh. */
    private _BindPlaceRefresh(): void {
        if (this._placeRefresh) {
            this._placeRefresh.unsubscribe();
            this._placeRefresh = null;
        }

        const _place = this.place;
        if (!_place) {
            return;
        }

        this._placeRefresh = _place.OnRefresh.subscribe(() => {
            this.change.markForCheck();
        });
    }

    private _SyncFloorReady(stage: number): void {
        if (stage >= 2) {
            this._floorReady = true;
        }
    }

    /**
     * Maps shared topbar waiter navigation (`placetables`) onto the PTF Mesas tab.
     */
    private _NormalizeTopbarTablesTab(): void {
        if (this.view.MainTab === 'placetables') {
            this.view.SetMainTab('VIEW', 'tables');
        }
    }

    OnTab(tabname: string): void {
        if (!this._IsWorkTab(tabname)) {
            return;
        }
        if (tabname === 'tickets' && this.tab !== 'tickets') {
            // Opening Tickets from the tab bar clears a mesa-driven filter.
            this._tableFilter = null;
        }
        this.view.SetMainTab('VIEW', tabname);
        this._SyncDeferredPanes(tabname);
        this.change.markForCheck();
    }

    /**
     * Applies supported PlacePanel navigations; ignores management destinations
     * (RN-PTF03-03). Cart is supported (task 08).
     */
    async OnPanel(panel: PlacePanel): Promise<boolean> {
        if (!this._IsSupportedPanel(panel)) {
            console.warn('[ptf-work] unsupported panel ignored', panel);
            return false;
        }

        const allowed = await this.view.RequestPanel(panel);
        if (allowed) {
            this.change.markForCheck();
        }
        return allowed;
    }

    private _IsSupportedPanel(panel: PlacePanel): boolean {
        // Args-only refresh while the ticket panel is open (ticket-edit Copy cycle).
        if (!('main' in panel) && !('scnd' in panel) && ('args' in panel)) {
            return true;
        }

        const main = panel.main ?? null;

        if (main === 'edit') {
            return false;
        }

        if (main === 'cart') {
            if (!('scnd' in panel)) {
                return true;
            }
            const cartScnd = panel.scnd ?? null;
            return cartScnd === 'ticket' || cartScnd === 'product' || cartScnd === null;
        }

        if (main !== null && main !== 'view') {
            return false;
        }

        if (!('scnd' in panel)) {
            return main === 'view' || main === null;
        }

        const scnd = panel.scnd ?? null;
        return scnd === 'ticket' || scnd === 'table' || scnd === null;
    }

    private _IsWorkTab(value: string | null): value is PtfWorkTab {
        return !!value && (_workTabs as ReadonlyArray<string>).includes(value);
    }

    private _SyncDeferredPanes(tab: PtfWorkTab): void {
        if (tab === 'tickets') {
            this._ticketsMounted = true;
        }
    }
}
